import { useEffect, useState, useCallback, useRef, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate, Outlet } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import FloatingButtons from "@/components/layout/FloatingButtons";
import { CountryProvider } from "@/context/CountryContext";

// Lazy-load ExitIntentPopup — only triggers after 8s, saves ~12KB from initial bundle
const ExitIntentPopup = lazy(() => import("@/components/ExitIntentPopup"));

// Lazy-loaded pages for Core Web Vitals optimization
const Home = lazy(() => import("@/pages/Home"));
const About = lazy(() => import("@/pages/About"));
const Services = lazy(() => import("@/pages/Services"));
const ServiceDetail = lazy(() => import("@/pages/ServiceDetail"));
const Contact = lazy(() => import("@/pages/Contact"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const LegalServices = lazy(() => import("@/pages/LegalServices"));
const LegalDetail = lazy(() => import("@/pages/LegalDetail"));
const CityLanding = lazy(() => import("@/pages/CityLanding"));
const CityIndustryLanding = lazy(() => import("@/pages/CityIndustryLanding"));
const Cities = lazy(() => import("@/pages/Cities"));
const Industries = lazy(() => import("@/pages/Industries"));
const IndustryLanding = lazy(() => import("@/pages/IndustryLanding"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const ClientDashboard = lazy(() => import("@/pages/ClientDashboard"));
const Login = lazy(() => import("@/pages/Login"));
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));
const ServiceIndustryCity = lazy(() => import("@/pages/ServiceIndustryCity"));
const ServiceCityLanding = lazy(() => import("@/pages/ServiceCityLanding"));
const IntentServiceCity = lazy(() => import("@/pages/IntentServiceCity"));
const NotFound = lazy(() => import("@/pages/NotFound"));

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#050505]">
      <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// Redirect old routes (without country prefix) to /in/...
function RedirectToIndia() {
  const location = useLocation();
  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href={`https://brandurbusiness.com/in${location.pathname}${location.search}`} />
      </Helmet>
      <Navigate to={`/in${location.pathname}${location.search}`} replace />
    </>
  );
}

// Country layout wrapper - India-only. Any non-"in" prefix redirects to the /in equivalent
// (noindex + canonical so already-indexed non-India URLs collapse to their India counterpart).
function CountryGuard() {
  const location = useLocation();
  const parts = location.pathname.split('/').filter(Boolean);
  const country = (parts[0] || '').toLowerCase();
  if (country !== 'in') {
    const rest = parts.slice(1).join('/');
    const target = `/in${rest ? '/' + rest : ''}${location.search}`;
    return (
      <>
        <Helmet>
          <meta name="robots" content="noindex, nofollow" />
          <link rel="canonical" href={`https://brandurbusiness.com${target}`} />
        </Helmet>
        <Navigate to={target} replace />
      </>
    );
  }
  return (
    <CountryProvider>
      <Outlet />
    </CountryProvider>
  );
}

function AppRouter() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  return <AppContent user={user} setUser={setUser} authLoading={authLoading} setAuthLoading={setAuthLoading} />;
}

function AppContent({ user, setUser, authLoading, setAuthLoading }) {
  const navigate = useNavigate();
  const location = useLocation();
  const firstPageView = useRef(true);

  // Fire a Meta Pixel PageView on SPA route changes (the base pixel in index.html
  // already tracks the initial load, so skip the first run to avoid double-counting).
  useEffect(() => {
    if (firstPageView.current) { firstPageView.current = false; return; }
    if (window.fbq) window.fbq('track', 'PageView');
  }, [location.pathname]);

  const checkAuth = useCallback(async () => {
    try {
      const response = await fetch(`${API}/auth/me`, { credentials: 'include' });
      if (response.ok) setUser(await response.json());
    } catch {
      // Anonymous users get 401 here by design — fail silently.
    } finally { setAuthLoading(false); }
  }, [setUser, setAuthLoading]);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const handleLogout = async () => {
    await fetch(`${API}/auth/logout`, { method: 'POST', credentials: 'include' });
    setUser(null);
    navigate('/in');
  };

  const isPublicPage = !location.pathname.startsWith('/dashboard') && !location.pathname.startsWith('/admin');

  return (
    <div className="min-h-screen bg-[#050505]">
      <ScrollToTop />
      {isPublicPage && <Navbar user={user} onLogout={handleLogout} />}
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Root redirect */}
          <Route path="/" element={<Navigate to="/in" replace />} />

          {/* Old routes redirect to /in/... (SEO-safe 301-like redirects) */}
          <Route path="/about" element={<RedirectToIndia />} />
          <Route path="/services" element={<RedirectToIndia />} />
          <Route path="/services/*" element={<RedirectToIndia />} />
          <Route path="/contact" element={<RedirectToIndia />} />
          <Route path="/blog" element={<RedirectToIndia />} />
          <Route path="/blog/*" element={<RedirectToIndia />} />
          <Route path="/pricing" element={<RedirectToIndia />} />
          <Route path="/legal" element={<RedirectToIndia />} />
          <Route path="/legal/*" element={<RedirectToIndia />} />
          <Route path="/cities" element={<RedirectToIndia />} />
          <Route path="/city/*" element={<RedirectToIndia />} />
          <Route path="/industries" element={<RedirectToIndia />} />
          <Route path="/industry/*" element={<RedirectToIndia />} />

          {/* Auth routes (no country prefix needed) */}
          <Route path="/login" element={<Login user={user} />} />
          <Route path="/auth/callback" element={<AuthCallback setUser={setUser} />} />
          <Route path="/dashboard/*" element={<ClientDashboard user={user} authLoading={authLoading} onLogout={handleLogout} />} />
          <Route path="/admin/*" element={<AdminDashboard user={user} authLoading={authLoading} onLogout={handleLogout} />} />

          {/* Country-prefixed routes (India-only; other prefixes redirect to /in) */}
          <Route path="/:country" element={<CountryGuard />}>
            <Route index element={<Home />} />
            <Route path="about" element={<About />} />
            <Route path="services" element={<Services />} />
            <Route path="services/:serviceId" element={<ServiceDetail />} />
            <Route path="services/:serviceId/:cityId" element={<ServiceCityLanding />} />
            <Route path="services/:serviceId/:industryId/:cityId" element={<ServiceIndustryCity />} />
            {/* Intent x Service x City programmatic pages: /in/{intent}/services/{service}/{city} */}
            <Route path=":intent/services/:serviceId/:cityId" element={<IntentServiceCity />} />
            <Route path="contact" element={<Contact />} />
            <Route path="blog" element={<Blog />} />
            <Route path="blog/:slug" element={<BlogPost />} />
            <Route path="pricing" element={<Pricing />} />
            <Route path="legal" element={<LegalServices />} />
            <Route path="legal/:categoryId" element={<LegalDetail />} />
            <Route path="cities" element={<Cities />} />
            <Route path="city/:cityId" element={<CityLanding />} />
            <Route path="city/:cityId/:industryId" element={<CityIndustryLanding />} />
            <Route path="industries" element={<Industries />} />
            <Route path="industry/:industryId" element={<IndustryLanding />} />
            <Route path="*" element={<NotFound />} />
          </Route>

          {/* Global catch-all 404 */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      {isPublicPage && <Footer />}
      <FloatingButtons />
      <Suspense fallback={null}>
        <ExitIntentPopup />
      </Suspense>
      <Toaster position="top-right" theme="dark" />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppRouter />
    </BrowserRouter>
  );
}

export default App;
